import React, { useState, useEffect } from "react";
import { useEditor } from "@craftjs/core";
import "../../styles/components/editor/SettingsPanel.css";

export const SettingsPanel = () => {
  const { selected, actions, query } = useEditor((state, query) => {
    const currentNodeId = state.events.selected;
    let selected;

    if (currentNodeId) {
      // Handle Set object properly - extract the first item from the set
      // This fixes the [object Set] warning
      const nodeId =
        currentNodeId instanceof Set
          ? currentNodeId.size > 0
            ? Array.from(currentNodeId)[0]
            : null
          : typeof currentNodeId === "string" ||
            typeof currentNodeId === "number"
          ? String(currentNodeId)
          : null;

      // Only proceed if we have a valid nodeId
      if (nodeId && state.nodes[nodeId]) {
        selected = {
          id: nodeId,
          name: state.nodes[nodeId].data.name,
          displayName:
            state.nodes[nodeId].data.custom?.displayName ||
            state.nodes[nodeId].data.displayName,
          hidden: state.nodes[nodeId].data.hidden,
          settings:
            state.nodes[nodeId].related && state.nodes[nodeId].related.settings,
          isDeletable: query.node(nodeId).isDeletable(),
          dom: query.node(nodeId).get().dom,
          data: state.nodes[nodeId].data,
          props: state.nodes[nodeId].data.props,
        };

        // Adding improved debugging - removed _craftComponent that doesn't exist on NodeData
        console.log("Selected node details:", {
          nodeId,
          name: state.nodes[nodeId].data.name,
          displayName: state.nodes[nodeId].data.displayName,
          related: state.nodes[nodeId].related,
          hasSettings: Boolean(
            state.nodes[nodeId].related && state.nodes[nodeId].related.settings
          ),
          props: state.nodes[nodeId].data.props,
        });

        // Check if there's no settings component despite the node having related field
        if (
          state.nodes[nodeId].related &&
          !state.nodes[nodeId].related.settings
        ) {
          console.warn(
            `Node ${nodeId} (${state.nodes[nodeId].data.name}) has related field but no settings component!`
          );
        }
      } else if (nodeId) {
        // Fixed: Properly handle missing node in state.nodes
        console.warn(
          `Node with ID ${nodeId} was selected but not found in state.nodes!`
        );
      }
    }

    return {
      selected,
    };
  });

  // Additional detailed debugging
  useEffect(() => {
    if (selected) {
      console.log("SettingsPanel: Component selected", {
        id: selected.id,
        name: selected.name,
        hasSettings: Boolean(selected.settings),
        // Fix for TypeScript error - check if settingsComponent exists and has a displayName
        settingsComponent: selected.settings
          ? (typeof selected.settings === "function" &&
              selected.settings.displayName) ||
            "SettingsComponent"
          : null,
      });
    } else {
      console.log("SettingsPanel: No component selected");

      // Additional debugging to check the events state - avoiding Set object issues
      try {
        const selectedNodeId = query.getEvent("selected").first();
        if (selectedNodeId) {
          // Handle Set objects properly here as well
          const nodeId =
            selectedNodeId instanceof Set
              ? selectedNodeId.size > 0
                ? Array.from(selectedNodeId)[0]
                : null
              : selectedNodeId;

          if (nodeId) {
            console.log(
              "Selected node exists in events but not in panel:",
              nodeId
            );
          }
        }
      } catch (error) {
        console.log("Error checking selected events:", error);
      }
    }
  }, [selected, query]);

  const [settingsTab, setSettingsTab] = useState("appearance");

  // Determine component type for showing relevant tabs
  const componentType = selected
    ? selected.name === "Text"
      ? "text"
      : selected.name === "Button"
      ? "button"
      : selected.name === "Image"
      ? "image"
      : selected.name === "Container"
      ? "container"
      : "other"
    : null;

  // Common copy action
  const handleCopyComponent = () => {
    if (selected) {
      const json = query.node(selected.id).toNodeTree();
      localStorage.setItem("clipboard-component", JSON.stringify(json));
      alert("Component copied to clipboard!");
    }
  };

  // Duplicate action
  const handleDuplicateComponent = () => {
    if (selected) {
      const json = query.node(selected.id).toNodeTree();
      actions.addNodeTree(json, selected.id);
    }
  };

  // Toggle visibility
  const handleToggleVisibility = () => {
    if (selected) {
      actions.setProp(selected.id, (props: any) => {
        props.hidden = !selected.hidden;
      });
    }
  };

  // Function to try re-selecting component if settings are not showing
  const handleRefreshSettings = () => {
    if (selected) {
      // Deselect and then reselect the component
      actions.selectNode(undefined);
      setTimeout(() => {
        actions.selectNode(selected.id);
      }, 100);
    }
  };

  return selected ? (
    <div className="settings-panel">
      <div className="settings-header">
        <h3>{selected.displayName || selected.name}</h3>
        <div className="component-actions">
          <button
            className="action-btn small"
            title="Copy component"
            onClick={handleCopyComponent}
          >
            📋 Copy
          </button>
          <button
            className="action-btn small"
            title="Duplicate component"
            onClick={handleDuplicateComponent}
          >
            🔄 Duplicate
          </button>
          <button
            className={`action-btn small ${selected.hidden ? "active" : ""}`}
            title={selected.hidden ? "Show component" : "Hide component"}
            onClick={handleToggleVisibility}
          >
            {selected.hidden ? "👁️‍🗨️ Show" : "👁️ Hide"}
          </button>
          {selected.isDeletable && (
            <button
              className="delete-btn small"
              onClick={() => actions.delete(selected.id)}
              title="Delete component"
            >
              🗑️ Delete
            </button>
          )}
        </div>
      </div>

      <div
        className="settings-tabs"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#fff",
          borderBottom: "1px solid #e0e0e0",
          paddingBottom: "8px",
        }}
      >
        <button
          className={`settings-tab-btn ${
            settingsTab === "appearance" ? "active" : ""
          }`}
          onClick={() => setSettingsTab("appearance")}
        >
          Appearance
        </button>

        {/* Typography tab shown for text elements */}
        {(componentType === "text" || componentType === "button") && (
          <button
            className={`settings-tab-btn ${
              settingsTab === "typography" ? "active" : ""
            }`}
            onClick={() => setSettingsTab("typography")}
          >
            Typography
          </button>
        )}

        {/* Decoration tab shown for buttons */}
        {componentType === "button" && (
          <button
            className={`settings-tab-btn ${
              settingsTab === "decoration" ? "active" : ""
            }`}
            onClick={() => setSettingsTab("decoration")}
          >
            Decoration
          </button>
        )}

        {/* Margin tab for spacing */}
        <button
          className={`settings-tab-btn ${
            settingsTab === "margin" ? "active" : ""
          }`}
          onClick={() => setSettingsTab("margin")}
        >
          Margin
        </button>
      </div>

      <div className="settings-content">
        {/* Appearance Tab - Shown for all components */}
        {settingsTab === "appearance" && (
          <div className="settings-properties">
            {/* Display name editor */}
            <div className="settings-field">
              <label>Display Name</label>
              <input
                type="text"
                value={selected.displayName || selected.name}
                onChange={(e) => {
                  actions.setCustom(selected.id, (custom: any) => {
                    custom.displayName = e.target.value;
                  });
                }}
              />
            </div>

            {/* Background Color */}
            {componentType !== "text" && (
              <div className="settings-field">
                <label>Background Color</label>
                <div className="color-input-container">
                  <input
                    type="color"
                    value={selected.props?.backgroundColor || "#ffffff"}
                    onChange={(e) => {
                      actions.setProp(selected.id, (props: any) => {
                        props.backgroundColor = e.target.value;
                      });
                    }}
                  />
                  <input
                    type="text"
                    value={selected.props?.backgroundColor || ""}
                    onChange={(e) => {
                      actions.setProp(selected.id, (props: any) => {
                        props.backgroundColor = e.target.value;
                      });
                    }}
                    placeholder="#ffffff or transparent"
                  />
                </div>
              </div>
            )}

            {/* Border Radius */}
            <div className="settings-field">
              <label>Border Radius</label>
              <div className="slider-with-value">
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={selected.props?.borderRadius || 0}
                  onChange={(e) => {
                    actions.setProp(selected.id, (props: any) => {
                      props.borderRadius = parseInt(e.target.value);
                    });
                  }}
                />
                <input
                  type="number"
                  value={selected.props?.borderRadius || 0}
                  onChange={(e) => {
                    actions.setProp(selected.id, (props: any) => {
                      props.borderRadius = parseInt(e.target.value);
                    });
                  }}
                  min="0"
                  max="50"
                />
              </div>
            </div>

            {/* Width and Height */}
            <div className="settings-field">
              <label>Width</label>
              <input
                type="text"
                value={selected.props?.width || ""}
                onChange={(e) => {
                  actions.setProp(selected.id, (props: any) => {
                    props.width = e.target.value;
                  });
                }}
                placeholder="e.g., 100%, 200px"
              />
            </div>

            <div className="settings-field">
              <label>Height</label>
              <input
                type="text"
                value={selected.props?.height || ""}
                onChange={(e) => {
                  actions.setProp(selected.id, (props: any) => {
                    props.height = e.target.value;
                  });
                }}
                placeholder="e.g., auto, 200px"
              />
            </div>

            {/* Component specific settings with enhanced styling */}
            <div
              className={`settings-specific-section ${
                componentType === "text"
                  ? "text-component-settings"
                  : componentType === "button"
                  ? "button-component-settings"
                  : componentType === "image"
                  ? "image-component-settings"
                  : componentType === "container"
                  ? "container-component-settings"
                  : ""
              }`}
            >
              <h4>
                {componentType === "text"
                  ? "Text Component Settings"
                  : componentType === "button"
                  ? "Button Component Settings"
                  : componentType === "image"
                  ? "Image Component Settings"
                  : componentType === "container"
                  ? "Container Component Settings"
                  : "Component Specific Settings"}
              </h4>

              {selected.settings && React.createElement(selected.settings)}
              {!selected.settings && (
                <div className="no-settings-message">
                  <p>
                    <strong>Element type:</strong>{" "}
                    {selected.data.displayName || selected.data.name}
                  </p>
                  <p>No settings component found for this element type.</p>
                </div>
              )}

              {/* Add refresh settings button for troubleshooting */}
              {!selected.settings && (
                <button
                  className="action-btn small"
                  onClick={handleRefreshSettings}
                  style={{ marginTop: "12px" }}
                >
                  🔄 Refresh Settings
                </button>
              )}
            </div>
          </div>
        )}

        {/* Typography Tab */}
        {settingsTab === "typography" &&
          (componentType === "text" || componentType === "button") && (
            <div className="settings-typography">
              <div className="settings-field">
                <label>Text</label>
                <textarea
                  value={selected.props?.text || ""}
                  onChange={(e) => {
                    actions.setProp(selected.id, (props: any) => {
                      props.text = e.target.value;
                    });
                  }}
                  rows={3}
                  placeholder="Enter text content here..."
                />
              </div>

              <div className="settings-field">
                <label>Font Size</label>
                <div className="slider-with-value">
                  <input
                    type="range"
                    min="8"
                    max="80"
                    value={selected.props?.fontSize || 16}
                    onChange={(e) => {
                      actions.setProp(selected.id, (props: any) => {
                        props.fontSize = parseInt(e.target.value);
                      });
                    }}
                  />
                  <input
                    type="number"
                    value={selected.props?.fontSize || 16}
                    onChange={(e) => {
                      actions.setProp(selected.id, (props: any) => {
                        props.fontSize = parseInt(e.target.value);
                      });
                    }}
                    min="8"
                    max="80"
                  />
                </div>
              </div>

              <div className="settings-field">
                <label>Font Weight</label>
                <select
                  value={selected.props?.fontWeight || "normal"}
                  onChange={(e) => {
                    actions.setProp(selected.id, (props: any) => {
                      props.fontWeight = e.target.value;
                    });
                  }}
                >
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                  <option value="lighter">Lighter</option>
                  <option value="bolder">Bolder</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="300">300</option>
                  <option value="400">400</option>
                  <option value="500">500</option>
                  <option value="600">600</option>
                  <option value="700">700</option>
                  <option value="800">800</option>
                  <option value="900">900</option>
                </select>
              </div>

              <div className="settings-field">
                <label>Text Alignment</label>
                <select
                  value={selected.props?.textAlign || "left"}
                  onChange={(e) => {
                    actions.setProp(selected.id, (props: any) => {
                      props.textAlign = e.target.value;
                    });
                  }}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                  <option value="justify">Justify</option>
                </select>
              </div>

              <div className="settings-field">
                <label>Text Color</label>
                <div className="color-input-container">
                  <input
                    type="color"
                    value={selected.props?.color || "#000000"}
                    onChange={(e) => {
                      actions.setProp(selected.id, (props: any) => {
                        props.color = e.target.value;
                      });
                    }}
                  />
                  <input
                    type="text"
                    value={selected.props?.color || ""}
                    onChange={(e) => {
                      actions.setProp(selected.id, (props: any) => {
                        props.color = e.target.value;
                      });
                    }}
                    placeholder="#000000"
                  />
                </div>
              </div>

              {/* Line Height */}
              <div className="settings-field">
                <label>Line Height</label>
                <div className="slider-with-value">
                  <input
                    type="range"
                    min="0.8"
                    max="3"
                    step="0.1"
                    value={selected.props?.lineHeight || 1.5}
                    onChange={(e) => {
                      actions.setProp(selected.id, (props: any) => {
                        props.lineHeight = parseFloat(e.target.value);
                      });
                    }}
                  />
                  <input
                    type="number"
                    value={selected.props?.lineHeight || 1.5}
                    onChange={(e) => {
                      actions.setProp(selected.id, (props: any) => {
                        props.lineHeight = parseFloat(e.target.value);
                      });
                    }}
                    min="0.8"
                    max="3"
                    step="0.1"
                  />
                </div>
              </div>
            </div>
          )}

        {/* Decoration Tab - Specific for buttons */}
        {settingsTab === "decoration" && componentType === "button" && (
          <div className="settings-decoration">
            <div className="settings-field">
              <label>Button URL</label>
              <input
                type="text"
                value={selected.props?.url || ""}
                onChange={(e) => {
                  actions.setProp(selected.id, (props: any) => {
                    props.url = e.target.value;
                  });
                }}
                placeholder="e.g., https://example.com"
              />
            </div>

            <div className="settings-field">
              <label>Open in New Tab</label>
              <input
                type="checkbox"
                checked={selected.props?.newTab || false}
                onChange={(e) => {
                  actions.setProp(selected.id, (props: any) => {
                    props.newTab = e.target.checked;
                  });
                }}
              />
            </div>

            <div className="settings-field">
              <label>Button Style</label>
              <select
                value={selected.props?.buttonStyle || "solid"}
                onChange={(e) => {
                  actions.setProp(selected.id, (props: any) => {
                    props.buttonStyle = e.target.value;
                  });
                }}
              >
                <option value="solid">Solid</option>
                <option value="outline">Outline</option>
                <option value="link">Link</option>
              </select>
            </div>

            <div className="settings-field">
              <label>Hover Effect</label>
              <select
                value={selected.props?.hoverEffect || "none"}
                onChange={(e) => {
                  actions.setProp(selected.id, (props: any) => {
                    props.hoverEffect = e.target.value;
                  });
                }}
              >
                <option value="none">None</option>
                <option value="darken">Darken</option>
                <option value="lighten">Lighten</option>
                <option value="shadow">Shadow</option>
                <option value="scale">Scale Up</option>
                <option value="glow">Glow Effect</option>
              </select>
            </div>
          </div>
        )}

        {/* Margin Tab - For all components */}
        {settingsTab === "margin" && (
          <div className="settings-margin">
            <div className="spacing-editor">
              <h4>Spacing Controls</h4>

              <div className="settings-field">
                <label>Margin</label>
                <input
                  type="text"
                  className="form-control"
                  value={selected.props?.margin || ""}
                  onChange={(e) => {
                    actions.setProp(selected.id, (props: any) => {
                      props.margin = e.target.value;
                    });
                  }}
                  placeholder="e.g., 10px or 10px 5px 10px 5px"
                />
              </div>

              <div className="spacing-visualization">
                <div className="margin-box">
                  <label>Margin</label>
                  <div className="padding-box">
                    <label>Padding</label>
                    <div className="content-box">
                      <label>Content</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-field">
                <label>Padding</label>
                <input
                  type="text"
                  className="form-control"
                  value={selected.props?.padding || ""}
                  onChange={(e) => {
                    actions.setProp(selected.id, (props: any) => {
                      props.padding = e.target.value;
                    });
                  }}
                  placeholder="e.g., 10px or 10px 5px 10px 5px"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className="settings-panel">
      <div className="settings-empty">
        <h3>No component selected</h3>
        <p>
          Select any text, button, image or container in the canvas to customize
          its properties.
        </p>
        <div className="empty-illustration">
          <span>👆</span>
        </div>
      </div>
    </div>
  );
};
